<template>
  <div class="base_keys_box">
    <div class="keys_box">
      <template v-if="tab == BaseTabs.mouse">
        <div class="mouse_box">
          <div class="mouse" v-if="specialCheck(speciaBehiver.SupportFireKey, DeviceManager.instance.state.currentPid)" :class="{ selected: actionSet.fireShow.value }" @click="selectFireKey()">
            <div class="mouse_icon_wrapper">
              <svg viewBox="0 0 24 24" class="mouse_icon">
                <path
                  d="M13 3.1c3.4.5 6 3.4 6 6.9v3c0 3.9-3.1 7-7 7s-7-3.1-7-7v-3c0-3.5 2.6-6.4 6-6.9v2.4h2V3.1zM11 4c-2.3.4-4 2.5-4 4.9h4V4zm6 4.9h-4v4.1h4v-4.1zM12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div class="mouse_label">{{ $t('fire_key') }}</div>
          </div>
          <div class="mouse" v-for="(key, index) in mouseBaseKeys()" :key="`mouse-${index}`" :class="{ selected: findKey(key) }" @click="selectKey(Number(key))">
            <div class="mouse_icon_wrapper">
              <svg viewBox="0 0 24 24" class="mouse_icon">
                <path :d="getMouseIconPath(Number(key))" fill="currentColor" />
              </svg>
            </div>
            <div class="mouse_label">{{ $t(mouseVKs[key]) }}</div>
          </div>
        </div>
      </template>

      <template v-else>
        <template v-if="tab == BaseTabs.compose">
          <div class="key_grid modifier_grid">
            <div class="key" v-for="(key, index) in AssistKeys()" :key="`assist-${index}`" :class="{ selected: findKey(key) }" @click="selectAssistKey(Number(key))">
              {{ kbVKTexture[key] }}
            </div>
          </div>
          <div class="key_divider"></div>
        </template>

        <div class="key_grid">
          <div class="key" v-for="(key, index) in keysList" :key="`key-${index}`" :alt="key" :class="{ selected: findKey(key) }" @click="selectKey(Number(key))">
            {{ kbVKTexture[key] }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DevicePid, kks, VK } from '@/define/define'
import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { AssistKeys, BaseKeys, SpecialKeys, kbVKTexture, mouseVKs, mouseBaseKeys } from '@/define/vkcode'
import i18n from '@/languages/i18n'
import { getCurrentDevice } from '@/profiles/selector'
import { DeviceManager } from '@/service/device'
import { BaseTabs, SetType, actionSet } from '@/service/driver/action_set'
import { operate } from '@/service/driver/operate'
import { DeviceType } from '@libs/types/enum'
import { computed, onMounted, watch } from 'vue'
import { Ref, ref } from 'vue'
import { checkSupport } from './action_config'

const { t } = i18n.global

const list: Ref<number[]> = ref(BaseKeys())
const unsupportKeys = ref<number[]>([])
const mer68SelfDevFnHiddenKeys = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57] // mer68se自研版本fn层需要隐藏的按键（数字键0~9）

const props = defineProps<{ tab: BaseTabs; setType?: SetType }>()
let tabTexts = ref({} as Map<BaseTabs, string>)
onMounted(() => {
  tabTexts.value = new Map()
  handleTabMouseAreaVisibility()
  tabTexts.value.set(BaseTabs.base, t('action_set_base_chat'))
  handleTabExtendVisibility()
  handleComposeVisibility()
})

const keysList = computed(() => {
  let ret = list.value.filter((key) => !unsupportKeys.value.includes(key))
  const isBaseKeySource = props.tab == BaseTabs.base || props.tab == BaseTabs.compose

  const isMer68SESelfDevFnLayer = specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMagnet) && operate.fnCheck.value
  if (isMer68SESelfDevFnLayer && isBaseKeySource) {
    ret = ret.filter((key) => !mer68SelfDevFnHiddenKeys.includes(key))
  }

  return ret
})

const isSupportMouseTab = computed(() => checkSupport.isMouseTabDevice())

const handleTabMouseAreaVisibility = () => {
  if (checkSupport.mouse()) {
    tabTexts.value.set(BaseTabs.mouse, t('action_set_mouse_key'))
  }
}

const handleTabExtendVisibility = () => {
  if (actionSet.setType.value != SetType.kme) {
    tabTexts.value.set(BaseTabs.extend, t('action_set_special_chat'))
    return
  }
  // 当前设备是royuan 厂商和晶泰厂商时
  if (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) || specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1)) {
    tabTexts.value.set(BaseTabs.extend, t('action_set_special_chat'))
  }
}

const handleComposeVisibility = () => {
  if (checkSupport.compose()) {
    tabTexts.value.set(BaseTabs.compose, t('action_set_combination_key'))
  }
}

const changeTab = (value: BaseTabs) => {
  changeList(value)
  actionSet.changeBaseTab(value)
  actionSet.changeSelectedKeys([0, 0, 0])
}

// 根据setType,处理扩展字符中的数据
const processExtendedCharsByType = () => {
  if (actionSet.setType.value == SetType.routine) {
    return SpecialKeys()
  }
  // 高级改键中，过滤到特殊键（例如：fn）
  const extendKeys = SpecialKeys().filter((v) => v != kks.value[getCurrentDevice.value.inoperableKeys[0]])

  // kme 和 rs 只显示当前键盘支持的扩展键
  if (actionSet.setType.value == SetType.kme || actionSet.setType.value == SetType.rs) {
    return Object.values(kks.value).filter((v) => extendKeys.includes(v))
  }
  return extendKeys
}

const changeList = (value: BaseTabs) => {
  if (value == BaseTabs.base || value == BaseTabs.compose) {
    list.value = BaseKeys()
  } else if (value == BaseTabs.extend) {
    // 确保这里使用了 else if
    list.value = processExtendedCharsByType()
  } else if (value == BaseTabs.mouse) {
    list.value = mouseBaseKeys()
  }
}

watch(
  () => props.tab,
  (value) => {
    changeList(value)
    if (value != BaseTabs.mouse) {
      actionSet.fireShow.value = false
    }
  },
  { immediate: true },
)

watch(
  () => actionSet.setType.value,
  () => {
    changeList(props.tab)
  },
)

const getMouseIconPath = (id: number) => {
  const paths: Record<number, string> = {
    // 0x01: Left Click (Base + Left highlight)
    0x01: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm-1 2.1c-2.3.4-4 2.5-4 4.9h4V4.1z M6 10v4c0 3.3 2.7 6 6 6s6-2.7 6-6v-4H6z',
    // 0x02: Right Click (Base + Right highlight)
    0x02: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm1 2.1v4.9h4c0-2.4-1.7-4.5-4-4.9z M6 10v4c0 3.3 2.7 6 6 6s6-2.7 6-6v-4H6z',
    // 0x04: Wheel/Middle (Base + Wheel highlight)
    0x04: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm1 4h-2v3h2V6zm5 4H6v4c0 3.3 2.7 6 6 6s6-2.7 6-6v-4z',
    // 0x05: Forward
    0x05: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm4 8l-4-4v3H8v2h4v3l4-4z',
    // 0x06: Backward
    0x06: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm-4 8l4 4v-3h4v-2h-4V7l-4 4z',
    // 0x98: DPI +
    0x98: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8zm1-12h-2v3H8v2h3v3h2v-3h3v-2h-3V8z',
    // 0x99: DPI -
    0x99: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8zm5-7H7v2h10v-2z',
    // 0x9a: DPI Loop
    0x9a: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8zm-1-6H9l3-3 3 3h-2v2h-2v-2z',
    // 0x9c: Wheel up
    0x9c: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm0 3l-3 3h2v4h2V8h2l-3-3z',
    // 0x9d: Wheel down
    0x9d: 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm0 14l3-3h-2V9h-2v4h-2l3 3z',
    // Moves
    0x9e: 'M12 8l-4 4h8l-4-4z', // Up
    0x9f: 'M12 16l4-4H8l4 4z', // Down
    0x89: 'M8 12l4 4V8l-4 4z', // Left
    0x8a: 'M16 12l-4-4v8l4-4z', // Right
  }
  return paths[id] || 'M12 2c4.4 0 8 3.6 8 8v4c0 4.4-3.6 8-8 8s-8-3.6-8-8v-4c0-4.4 3.6-8 8-8zm0 14c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z'
}

const findKey = (key: number): boolean => {
  return actionSet.baseKeys.selectedKeys.includes(key)
}

// selectedAssist 只能保存两个
const selectAssistKey = (key: VK) => {
  const newKeys: VK[] = [...actionSet.baseKeys.selectedKeys]
  if (findKey(key)) {
    newKeys[newKeys.indexOf(key)] = 0
  } else {
    if (newKeys[0] == undefined || newKeys[0] == 0) {
      newKeys[0] = key
    } else if (newKeys[0] > 0) {
      newKeys[1] = key
    }
  }

  actionSet.changeSelectedKeys(newKeys)
}

const selectKey = (key: number) => {
  const newKeys: VK[] = [...actionSet.baseKeys.selectedKeys]
  if (props.tab != BaseTabs.compose) {
    newKeys[0] = 0
    newKeys[1] = 0
  }
  newKeys[2] = key
  actionSet.changeSelectedKeys(newKeys)
  actionSet.fireShow.value = false
}

//选中火力键
const selectFireKey = () => {
  actionSet.fireShow.value = true
  actionSet.changeSelectedKeys([0, 0, 0])
}
</script>

<style lang="less" scoped>
.base_keys_box {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;

  .tabs {
    display: flex;
    gap: 24px;
    padding: 0 4px;
    background: transparent;
    border-bottom: 1px solid var(--border-color);
    width: 100%;
    margin-bottom: 4px;

    .tab {
      position: relative;
      padding: 8px 12px;
      font-size: 14px;
      color: var(--color-text-hint);
      cursor: pointer;
      transition: var(--transition-base);

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 50%;
        transform: translateX(-50%) scaleX(0);
        width: 60%;
        height: 3px;
        background: var(--brand-color-pure);
        border-radius: 100px;
        transition: var(--transition-base);
      }

      &:hover {
        color: var(--color-dark);
      }

      &.selected {
        color: var(--brand-color-pure);
        font-weight: 600;

        &::after {
          transform: translateX(-50%) scaleX(1);
        }
      }
    }
  }

  .keys_box {
    flex: 1;
    // padding: 4px 0;
    display: flex;
    flex-direction: column;
    // gap: 8px;
    padding: var(--spacing-inner-padding);
    text-align: center;

    .key_grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, 46px); /* 自动填充，每列固定 46px */
      gap: 6px;
      justify-content: flex-start;

      &.modifier_grid {
        grid-template-columns: repeat(auto-fill, 62px);
        margin-bottom: 10px;
        // background: var(--color-gray-1);
        // border-radius: 8px;
        // width: 100%;

        .key {
          font-size: 12px;
        }
      }
    }

    .key {
      // width: 45px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--glass-surface-bg);
      border-radius: var(--radius-m);
      font-size: 13px; /* 增大按键尺寸后，字体也适度调大，提高辨识度 */
      font-weight: 500;
      color: var(--color-text-hint);
      cursor: pointer;
      transition: var(--transition-base);
      outline: none;
      border: 1px solid rgba(255, 255, 255, 0.1);

      &:hover:not(.selected) {
        background: var(--color-white);
        border-color: var(--brand-color-pure);
        color: var(--brand-color-pure);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
      }

      &:active {
        transform: translateY(0);
      }

      &.selected {
        background: var(--brand-color) !important;
        border-color: transparent;
        color: var(--color-white) !important;
        font-weight: 600;
        box-shadow: var(--action-btn-primary-shadow);
      }
    }

    .key_divider {
      display: none;
    }
  }

  .mouse_box {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
    gap: var(--spacing-grid-gap);
    width: 100%;

    .mouse {
      aspect-ratio: 1.1 / 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: var(--bg-card, rgba(255, 255, 255, 0.9));
      border-radius: var(--radius-s);
      padding: 12px 8px;
      text-align: center;
      outline: none;
      /* 应用统一卡片效果 */
      .mgx_card_item_effect();

      .mouse_icon_wrapper {
        width: 32px;
        height: 32px;
        background: var(--color-gray-1);
        border-radius: var(--radius-s);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: var(--transition-base);
        color: var(--color-dark);

        .mouse_icon {
          width: 18px;
          height: 18px;
          opacity: 0.8;
          transition: var(--transition-base);
        }
      }

      .mouse_label {
        font-size: 11px;
        color: var(--color-text-hint);
        font-weight: 500;
        transition: var(--transition-base);
        text-align: center;
        line-height: 1.2;
        width: 100%;
        white-space: normal;
        word-break: break-all;
      }
    }
  }
}
</style>
